/**
 * The Chats page's side column (ADR 0024 §3): the person's own arrangement of
 * Fan panel blocks and widgets in two areas, Top and the Dock under it. Kept
 * in the person's Workspace settings on the server (`chatSideColumn`), edited
 * in Settings → Styling.
 */

export const SIDE_BLOCK_IDS = [
  'overview',
  'spending',
  'spendAcross',
  'lists',
  'notes',
  'aiReasoning',
  'aiSummary',
  'onlineSpenders',
  'activity',
  'feed',
  'active',
] as const
export type SideBlockId = (typeof SIDE_BLOCK_IDS)[number]
export type SideArea = 'top' | 'dock'

export interface SideBlock {
  id: SideBlockId
  shown: boolean
  area: SideArea
}

/** The key in crm_workspace_preferences.settings. */
export const SIDE_SETTINGS_KEY = 'chatSideColumn'

/** Workspace widgets: they cover the person's Scope, not the open chat. */
export const SIDE_WIDGETS: ReadonlySet<SideBlockId> = new Set(['activity', 'feed', 'active'])

const OFF_BY_DEFAULT: ReadonlySet<SideBlockId> = SIDE_WIDGETS
const DOCK_BY_DEFAULT: ReadonlySet<SideBlockId> = new Set(['onlineSpenders', 'activity', 'feed', 'active'])

export const SIDE_DEFAULT: readonly SideBlock[] = SIDE_BLOCK_IDS.map((id) => ({
  id,
  shown: !OFF_BY_DEFAULT.has(id),
  area: DOCK_BY_DEFAULT.has(id) ? 'dock' : 'top',
}))

const isId = (v: unknown): v is SideBlockId => (SIDE_BLOCK_IDS as readonly string[]).includes(v as string)

/** The saved arrangement, cleaned: known blocks once each in saved order, new blocks appended with their defaults. */
export function sideColumnFrom(raw: unknown): SideBlock[] {
  const saved = raw && typeof raw === 'object' && Array.isArray((raw as { blocks?: unknown }).blocks) ? (raw as { blocks: unknown[] }).blocks : []
  const out: SideBlock[] = []
  const seen = new Set<SideBlockId>()
  for (const item of saved) {
    const b = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
    if (!isId(b.id) || seen.has(b.id)) continue
    const def = SIDE_DEFAULT.find((d) => d.id === b.id)!
    seen.add(b.id)
    out.push({
      id: b.id,
      shown: typeof b.shown === 'boolean' ? b.shown : def.shown,
      area: b.area === 'top' || b.area === 'dock' ? b.area : def.area,
    })
  }
  for (const def of SIDE_DEFAULT) if (!seen.has(def.id)) out.push({ ...def })
  return out
}

export function inArea(blocks: readonly SideBlock[], area: SideArea): SideBlock[] {
  return blocks.filter((b) => b.area === area)
}

/** Swap with the previous (-1) or next (+1) block of the same area. */
export function moveBlock(blocks: readonly SideBlock[], id: SideBlockId, delta: -1 | 1): SideBlock[] {
  const i = blocks.findIndex((b) => b.id === id)
  if (i < 0) return [...blocks]
  const area = blocks[i]!.area
  let j = i + delta
  while (j >= 0 && j < blocks.length && blocks[j]!.area !== area) j += delta
  if (j < 0 || j >= blocks.length) return [...blocks]
  const next = [...blocks]
  ;[next[i], next[j]] = [next[j]!, next[i]!]
  return next
}

/** Put a block in the other area, at its end. */
export function setArea(blocks: readonly SideBlock[], id: SideBlockId, area: SideArea): SideBlock[] {
  const block = blocks.find((b) => b.id === id)
  if (!block || block.area === area) return [...blocks]
  return [...blocks.filter((b) => b.id !== id), { ...block, area }]
}

export function toggleBlock(blocks: readonly SideBlock[], id: SideBlockId): SideBlock[] {
  return blocks.map((b) => (b.id === id ? { ...b, shown: !b.shown } : b))
}

/** Take a block off the column (it moves to "Add widgets"). */
export function removeBlock(blocks: readonly SideBlock[], id: SideBlockId): SideBlock[] {
  return blocks.map((b) => (b.id === id ? { ...b, shown: false } : b))
}

/** Put a block back on the column, last in `area` (its own area when not given). */
export function addBlock(blocks: readonly SideBlock[], id: SideBlockId, area?: SideArea): SideBlock[] {
  const block = blocks.find((b) => b.id === id)
  if (!block) return [...blocks]
  return [...blocks.filter((b) => b.id !== id), { ...block, shown: true, area: area ?? block.area }]
}

/**
 * Drop a block at `index` among the shown blocks of `area` (drag and drop).
 * Hidden blocks keep their places; the dropped block is shown.
 */
export function placeBlock(blocks: readonly SideBlock[], id: SideBlockId, area: SideArea, index: number): SideBlock[] {
  const block = blocks.find((b) => b.id === id)
  if (!block) return [...blocks]
  const moved = { ...block, shown: true, area }
  const rest = blocks.filter((b) => b.id !== id)
  const targets = rest.filter((b) => b.shown && b.area === area)
  const before = targets[Math.max(0, Math.min(index, targets.length))]
  if (!before) {
    const last = targets[targets.length - 1]
    const at = last ? rest.indexOf(last) + 1 : rest.length
    return [...rest.slice(0, at), moved, ...rest.slice(at)]
  }
  const at = rest.indexOf(before)
  return [...rest.slice(0, at), moved, ...rest.slice(at)]
}

/** Active chats in the side column: chats where the fan answered, or where we wait for the fan. */
export type ActiveMode = 'replied' | 'waiting'
export const ACTIVE_MODE_KEY = 'chatSideActiveMode'
export const activeModeFrom = (raw: unknown): ActiveMode => (raw === 'waiting' ? 'waiting' : 'replied')
